import type { MediaDescriptor } from "@aasaan-share/protocol";
import {
  MemoryDownloadSink,
  safeDownloadFilename,
} from "./download-sink.ts";
import type { MediaRequestInput, MediaResult } from "./viewer-media.ts";

export type AlbumDownloadState =
  | "preparing"
  | "downloading"
  | "completed"
  | "cancelled"
  | "failed";

export interface AlbumDownloadProgress {
  readonly completed: number;
  readonly current: number;
  readonly failed: number;
  readonly progress: number;
  readonly state: AlbumDownloadState;
  readonly total: number;
}

export interface AlbumDownloadQueueOptions {
  readonly descriptors: readonly MediaDescriptor[];
  readonly onChange: (progress: AlbumDownloadProgress) => void;
  readonly requestOriginal: (input: MediaRequestInput) => Promise<MediaResult>;
  readonly cancelRequest: (requestId: string) => void;
  readonly save: (blob: Blob, filename: string) => void;
  readonly shareId: string;
}

export class AlbumDownloadQueue {
  readonly #options: AlbumDownloadQueueOptions;
  #activeRequestId: string | null = null;
  #cancelled = false;
  #progress: AlbumDownloadProgress;

  constructor(options: AlbumDownloadQueueOptions) {
    this.#options = options;
    this.#progress = {
      completed: 0,
      current: 0,
      failed: 0,
      progress: 0,
      state: "preparing",
      total: options.descriptors.length,
    };
  }

  cancel(): void {
    this.#cancelled = true;
    if (this.#activeRequestId) this.#options.cancelRequest(this.#activeRequestId);
  }

  updateProgress(requestId: string, progress: number): boolean {
    if (requestId !== this.#activeRequestId) return false;
    this.publish({
      ...this.#progress,
      progress: Math.min(1, Math.max(0, progress)),
    });
    return true;
  }

  async run(): Promise<AlbumDownloadProgress> {
    if (this.#progress.total === 0) {
      return this.finish("completed");
    }

    for (const [index, descriptor] of this.#options.descriptors.entries()) {
      if (this.#cancelled) break;
      const rendition = descriptor.renditions.original;
      if (!rendition) {
        this.publish({
          ...this.#progress,
          current: index + 1,
          failed: this.#progress.failed + 1,
        });
        continue;
      }

      const requestId = crypto.randomUUID().toLowerCase();
      this.#activeRequestId = requestId;
      this.publish({
        ...this.#progress,
        current: index + 1,
        progress: 0,
        state: "downloading",
      });
      try {
        const result = await this.#options.requestOriginal({
          descriptor,
          rendition: "original",
          requestId,
          shareId: this.#options.shareId,
          sink: new MemoryDownloadSink(rendition.mimeType, rendition.byteLength),
        });
        if (result.blob) {
          this.#options.save(
            result.blob,
            safeDownloadFilename(descriptor.mediaId, result.mimeType),
          );
        }
        if (!this.#cancelled) {
          this.publish({
            ...this.#progress,
            completed: this.#progress.completed + 1,
            progress: 1,
          });
        }
      } catch {
        if (!this.#cancelled) {
          this.publish({
            ...this.#progress,
            failed: this.#progress.failed + 1,
            progress: 0,
          });
        }
      } finally {
        if (this.#activeRequestId === requestId) this.#activeRequestId = null;
      }
    }

    return this.finish(
      this.#cancelled
        ? "cancelled"
        : this.#progress.failed > 0
          ? "failed"
          : "completed",
    );
  }

  private finish(state: AlbumDownloadState): AlbumDownloadProgress {
    this.publish({ ...this.#progress, progress: 0, state });
    return this.#progress;
  }

  private publish(progress: AlbumDownloadProgress): void {
    this.#progress = progress;
    this.#options.onChange(progress);
  }
}
